import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { provisioningFetch } from './ProvisioningGate';

const API_BASE = process.env.REACT_APP_API_BASE ?? '';

const isIOSDevice = () => {
  const userAgent = navigator.userAgent;
  const platform = navigator.platform;
  const maxTouchPoints = navigator.maxTouchPoints ?? 0;
  return /iPad|iPhone|iPod/i.test(userAgent)
    || (platform === 'MacIntel' && maxTouchPoints > 1);
};

function LockerAsignmentPag() {
  const scannerRef = useRef<any>(null);
  const lastDecodedTextRef = useRef('');
  const lastDecodedAtRef = useRef(0);
  const [scannerStatus, setScannerStatus] = useState('Starting camera...');
  const [lastScanAt, setLastScanAt] = useState<number | null>(null);
  const [scannedContent, setScannedContent] = useState<string | null>(null);
  const [isTorchSupported, setIsTorchSupported] = useState(false);
  const [isTorchEnabled, setIsTorchEnabled] = useState(false);
  const [isTorchBusy, setIsTorchBusy] = useState(false);
  const [scanFlashActive, setScanFlashActive] = useState(false);

  const navigate = useNavigate();
  const isResolvingRef = useRef(false);
  const [resolveError, setResolveError] = useState<string | null>(null);
  const [manualUrl, setManualUrl] = useState('');

  const maybeResolveProvisionUrl = async (url: string) => {
    if (!url.startsWith('http://') && !url.startsWith('https://')) return;
    if (isResolvingRef.current) return;

    isResolvingRef.current = true;
    setResolveError(null);
    // Capture timestamp before any async work: the Eloxx event must arrive AFTER this moment.
    const after = new Date().toISOString();

    try {
      const resp = await provisioningFetch(`${API_BASE}/provision/resolve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url }),
      });

      if (resp.status === 404) {
        const body = await resp.json().catch(() => ({})) as { error?: string };
        throw new Error(body.error ?? 'URL not recognised as a locker QR code.');
      }
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({})) as { error?: string };
        throw new Error(body.error ?? `HTTP ${resp.status}`);
      }

      const context = await resp.json();
      navigate('/pair-locker', { state: { context, after } });
    } catch (err) {
      setResolveError(err instanceof Error ? err.message : 'Could not resolve QR code.');
    } finally {
      isResolvingRef.current = false;
    }
  };

  const refreshTorchState = () => {
    const qrScanner = scannerRef.current;
    if (!qrScanner) return;
    try {
      const capabilities = qrScanner.getRunningTrackCapabilities() as Record<string, unknown>;
      const torchSupported = Boolean(capabilities?.torch);
      setIsTorchSupported(torchSupported);
      if (!torchSupported) { setIsTorchEnabled(false); return; }
      const settings = qrScanner.getRunningTrackSettings() as Record<string, unknown>;
      setIsTorchEnabled(Boolean(settings?.torch));
    } catch {
      setIsTorchSupported(false);
      setIsTorchEnabled(false);
    }
  };

  const toggleTorch = async () => {
    const qrScanner = scannerRef.current;
    if (!qrScanner || !isTorchSupported || isTorchBusy) return;
    setIsTorchBusy(true);
    try {
      const nextTorchState = !isTorchEnabled;
      await qrScanner.applyVideoConstraints({ advanced: [{ torch: nextTorchState } as any] });
      setIsTorchEnabled(nextTorchState);
      setScannerStatus(nextTorchState ? 'Torch is on.' : 'Torch is off.');
    } catch {
      setScannerStatus('Unable to toggle torch on this device/browser.');
      refreshTorchState();
    } finally {
      setIsTorchBusy(false);
    }
  };

  useEffect(() => {
    if (!lastScanAt) return;
    setScanFlashActive(true);
    const timeoutId = window.setTimeout(() => setScanFlashActive(false), 1400);
    return () => window.clearTimeout(timeoutId);
  }, [lastScanAt]);

  useEffect(() => {
    let isMounted = true;

    const startScanner = async () => {
      try {
        if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
          setScannerStatus('Camera needs HTTPS on mobile browsers.');
          return;
        }

        setScannerStatus('Requesting camera permission...');
        const { Html5Qrcode, Html5QrcodeSupportedFormats } = await import('html5-qrcode');
        if (!isMounted) return;

        const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
        const isIOSScanner = isIOSDevice();

        const qrScanner = new Html5Qrcode('qr-reader', {
          formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
          // Keep software decoder for consistent behavior across Safari/Chrome variants.
          experimentalFeatures: { useBarCodeDetectorIfSupported: false },
        } as any);
        scannerRef.current = qrScanner;

        const config: Record<string, unknown> = {
          // Lower scan cadence to reduce processing pressure and dropped frames.
          fps: isIOSScanner ? 6 : isMobile ? 8 : 10,
          aspectRatio: isIOSScanner ? (4 / 3) : undefined,
          // Keep mirrored decode enabled for compatibility unless perf is proven problematic.
          disableFlip: false,
        };

        // Full-frame scanning on iPhone is less strict about perfect code centering.
        if (!isIOSScanner) {
          config.qrbox = (viewfinderWidth: number, viewfinderHeight: number) => {
            const baseRatio = isMobile ? 0.72 : 0.70;
            const rawSize = Math.round(Math.min(viewfinderWidth, viewfinderHeight) * baseRatio);
            const size = Math.max(220, rawSize);
            return {
              width: Math.min(size, viewfinderWidth),
              height: Math.min(size, viewfinderHeight),
            };
          };
        }

        const onSuccess = (decodedText: string) => {
          const now = Date.now();
          const isDuplicate =
            decodedText === lastDecodedTextRef.current && now - lastDecodedAtRef.current < 1500;
          if (isDuplicate) return;
          lastDecodedTextRef.current = decodedText;
          lastDecodedAtRef.current = now;
          setLastScanAt(now);
          setScannedContent(decodedText);
          setScannerStatus('QR code detected.');
          void maybeResolveProvisionUrl(decodedText);
        };

        const onError = () => { /* frame errors ignored */ };

        if (isMobile) {
          // Mobile: iOS Safari is more reliable with a camera-id fallback chain.
          const mobileStartAttempts: Array<unknown> = [
            { facingMode: { exact: 'environment' } },
            { facingMode: 'environment' },
            { facingMode: { ideal: 'environment' } },
          ];

          if (isIOSScanner) {
            const cameras = await Html5Qrcode.getCameras().catch(() => [] as Array<{ id: string; label: string }>);
            const rearCamera = cameras.find(cam => /back|rear|environment/i.test(cam.label));
            if (rearCamera?.id) {
              mobileStartAttempts.push(rearCamera.id);
            } else if (cameras.length > 1) {
              mobileStartAttempts.push(cameras[cameras.length - 1].id);
            } else if (cameras[0]?.id) {
              mobileStartAttempts.push(cameras[0].id);
            }
          }

          let started = false;
          let lastStartError: unknown = null;
          for (const source of mobileStartAttempts) {
            try {
              await qrScanner.start(source as any, config, onSuccess, onError);
              started = true;
              break;
            } catch (err) {
              lastStartError = err;
            }
          }

          if (!started) {
            throw lastStartError instanceof Error ? lastStartError : new Error('Unable to access the rear camera.');
          }

          // Best-effort autofocus hint for iOS where supported.
          if (isIOSScanner) {
            try {
              await qrScanner.applyVideoConstraints({
                advanced: [{ focusMode: 'continuous' } as any],
              });
            } catch {
              // Ignore when focusMode is not supported.
            }
          }
        } else {
          // Desktop: enumerate cameras and pick the first one
          const cameras = await Html5Qrcode.getCameras();
          if (!cameras || cameras.length === 0) {
            if (isMounted) setScannerStatus('No camera found on this device.');
            return;
          }
          await qrScanner.start(cameras[0].id, config, onSuccess, onError);
        }

        if (isMounted) {
          refreshTorchState();
          setScannerStatus('Ready');
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to start the scanner.';
        if (isMounted) setScannerStatus(`Unable to start camera: ${message}`);
      }
    };

    startScanner();

    return () => {
      isMounted = false;
      const qrScanner = scannerRef.current;
      scannerRef.current = null;
      if (qrScanner) {
        const clearScanner = () => { try { qrScanner.clear(); } catch { /* no-op */ } };

        try {
          const torchOff = qrScanner.applyVideoConstraints({ advanced: [{ torch: false } as any] });
          if (torchOff?.catch) torchOff.catch(() => {});
        } catch { /* no-op */ }

        try {
          const stop = qrScanner.stop();
          if (stop?.catch) stop.catch(() => {}).finally(() => clearScanner());
          else clearScanner();
        } catch { clearScanner(); }
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="App AssignPage">
      <header className="App-header">
        <div className="QrScannerCard QrScannerCardFull">
          <div className="QrReaderWrap QrReaderWrapFull">
            <div id="qr-reader" className="QrReader" />
          </div>
          <div className="QrOverlayBottom">
            <div className="ScannerBottomBar">
              <div
                className={`ScanFlash ${scanFlashActive ? 'is-active' : ''}`}
                role="status"
                aria-live="polite"
                data-scanner-status={scannerStatus}
              >
                {scanFlashActive ? 'NEW QR CODE SCANNED' : 'Ready to scan'}
              </div>
              {isTorchSupported && (
                <button className="TorchButton" type="button" onClick={toggleTorch} disabled={isTorchBusy}>
                  {isTorchBusy ? '...' : isTorchEnabled ? 'Torch off' : 'Torch on'}
                </button>
              )}
              {resolveError && <p className="ScannerError">{resolveError}</p>}
              {scannedContent && <p className="ScannerRead">{scannedContent}</p>}
              <div className="ManualUrlRow">
                <input
                  className="ManualUrlInput"
                  type="url"
                  placeholder="or paste URL..."
                  value={manualUrl}
                  onChange={e => setManualUrl(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && manualUrl.trim()) void maybeResolveProvisionUrl(manualUrl.trim()); }}
                />
                <button
                  className="ManualUrlGo"
                  type="button"
                  disabled={!manualUrl.trim() || isResolvingRef.current}
                  onClick={() => void maybeResolveProvisionUrl(manualUrl.trim())}
                >Go</button>
              </div>
            </div>
          </div>
        </div>
      </header>
    </div>
  );
}

export default LockerAsignmentPag;
